import { Controller } from "@hotwired/stimulus";

// Sidebar controller with desktop collapse/expand and mobile show/hide.
// Desktop (>=1024px): toggles between 4rem (icons) and 16rem (expanded).
// Mobile (<1024px): toggles between 0 (hidden) and 16rem (expanded).
export default class extends Controller {
  static targets = [
    "desktopSidebar",
    "backdrop",
  ];

  static values = {
    storageKey: { type: String, default: "sidebarExpanded" },
    forceCollapsed: { type: Boolean, default: false },
    // Icon rail (decision-75): on desktop the width is fixed at 5.5rem and
    // toggling does nothing. Below lg nothing changes.
    rail: { type: Boolean, default: false },
  };

  static RAIL_WIDTH = "5.5rem";

  connect() {
    this.boundActive = this._markActive.bind(this);
    window.addEventListener("turbo:load", this.boundActive);
    this._markActive();

    this.mediaQuery = window.matchMedia("(min-width: 1024px)");

    // The report designer (TASK-229.4) starts collapsed on every visit,
    // whatever the user's saved preference — a session-only override that
    // never reads or writes localStorage, so leaving the builder for any
    // other page restores exactly what they had there. forceCollapsed is a
    // plain data attribute the layout sets per-page (content_for
    // :collapse_sidebar), not a stored choice.
    if (this.forceCollapsedValue) {
      this.isExpanded = false;
      if (this.hasDesktopSidebarTarget) {
        this.desktopSidebarTarget.style.transition = "none";
        this._applyState();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.desktopSidebarTarget.style.transition = "";
          });
        });
      }
      this._updateToggleIcon();
      this.boundMediaChange = this._handleMediaChange.bind(this);
      this.mediaQuery.addEventListener("change", this.boundMediaChange);
      return;
    }

    // No stored preference (first visit, or storage cleared/blocked): default
    // to collapsed below the desktop breakpoint, so the drawer doesn't open
    // over the page on a first mobile visit. Desktop keeps its existing
    // default of expanded. An explicit stored choice always wins, whatever
    // the viewport.
    const stored = this._readStoredPreference();
    this.isExpanded = stored === null ? this._isDesktop : stored;

    if (this.hasDesktopSidebarTarget) {
      // Disable transition on load
      this.desktopSidebarTarget.style.transition = "none";
      this._applyState();

      // Re-enable transitions
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.desktopSidebarTarget.style.transition = "";
        });
      });
    }

    // Always sync hamburger icon state on connect
    this._updateToggleIcon();

    // Responsive breakpoint listener
    this.boundMediaChange = this._handleMediaChange.bind(this);
    this.mediaQuery.addEventListener("change", this.boundMediaChange);
  }

  disconnect() {
    if (this.boundActive) {
      window.removeEventListener("turbo:load", this.boundActive);
    }
    if (this.mediaQuery && this.boundMediaChange) {
      this.mediaQuery.removeEventListener("change", this.boundMediaChange);
    }
  }

  // —— Public actions ————————————————————————————————————

  toggle() {
    if (this._isDesktopRail) return;
    this.isExpanded = !this.isExpanded;
    this._applyState();
    this._maybeWriteStoredPreference(this.isExpanded);
  }

  open() {
    if (this._isDesktopRail) return;
    this.isExpanded = true;
    this._applyState();
    this._maybeWriteStoredPreference(true);
  }

  close() {
    if (this._isDesktopRail) return;
    this.isExpanded = false;
    this._applyState();
    this._maybeWriteStoredPreference(false);
  }

  // forceCollapsed pages (the report designer) never touch the user's saved
  // preference, even when they manually toggle the sidebar open for a
  // moment — the point is that leaving the builder restores exactly what
  // they had, not "whatever they last clicked while inside it".
  _maybeWriteStoredPreference(isExpanded) {
    if (this.forceCollapsedValue) return;

    this._writeStoredPreference(isExpanded);
  }

  closeIfMobile() {
    if (!this._isDesktop) {
      this.close();
    }
  }

  // —— Private ———————————————————————————————————————————

  get _isDesktop() {
    return this.mediaQuery.matches;
  }

  get _isDesktopRail() {
    return this.railValue && this._isDesktop;
  }

  // Marks the link matching the current path with aria-current="page" and
  // clears it from the rest. The sidebar is turbo-permanent, so this runs on
  // every turbo:load rather than being rendered by the server. Longest match
  // wins; data-sidebar-match-exact links need the whole path.
  _markActive() {
    const path = window.location.pathname;
    let best = null;
    let bestLength = -1;
    this.element.querySelectorAll("[data-sidebar-match]").forEach((link) => {
      const match = link.dataset.sidebarMatch;
      const exact = link.hasAttribute("data-sidebar-match-exact");
      const hit = exact
        ? path === match
        : path === match || path.startsWith(match + "/");
      if (hit && match.length > bestLength) {
        best = link;
        bestLength = match.length;
      }
    });
    this.element.querySelectorAll("[data-sidebar-match]").forEach((link) => {
      if (link === best) {
        link.setAttribute("aria-current", "page");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  }

  // Reads the stored expanded/collapsed choice, or null when there is none
  // (first visit) or storage is unavailable (private browsing, blocked site
  // data) — never lets a storage exception break the sidebar.
  _readStoredPreference() {
    try {
      const raw = localStorage.getItem(this.storageKeyValue);
      return raw === null ? null : raw !== "false";
    } catch (_) {
      return null;
    }
  }

  _writeStoredPreference(isExpanded) {
    try {
      localStorage.setItem(this.storageKeyValue, isExpanded.toString());
    } catch (_) {
      // Storage unavailable — the sidebar still works for this session, it
      // just won't remember the choice next visit.
    }
  }

  _applyState() {
    if (!this.hasDesktopSidebarTarget) return;

    if (this._isDesktopRail) {
      // Desktop rail: fixed width, whatever the expanded state.
      this.desktopSidebarTarget.style.width = this.constructor.RAIL_WIDTH;
      this.desktopSidebarTarget.style.borderRightWidth = "";
    } else if (this._isDesktop) {
      // Desktop: 4rem (collapsed icons) or 16rem (expanded)
      this.desktopSidebarTarget.style.width = this.isExpanded ? "16rem" : "4rem";
      this.desktopSidebarTarget.style.borderRightWidth = "";
    } else {
      // Mobile: fully hidden or 16rem (expanded)
      this.desktopSidebarTarget.style.width = this.isExpanded ? "16rem" : "0";
      this.desktopSidebarTarget.style.borderRightWidth = this.isExpanded ? "" : "0";
    }

    // A desktop rail is never "expanded": the sidebar-expanded: variant
    // (specificity 0,2,0) would otherwise beat the tiles' lg:hidden and show
    // the drawer's full labels on the rail too.
    this.desktopSidebarTarget.classList.toggle("sidebar-expanded", this.isExpanded && !this._isDesktopRail);
    this._updateBackdrop();
    this._updateToggleIcon();
  }

  _updateBackdrop() {
    if (!this.hasBackdropTarget) return;
    const showBackdrop = !this._isDesktop && this.isExpanded;
    this.backdropTarget.classList.toggle("active", showBackdrop);
  }

  _updateToggleIcon() {
    const pinIcon = this.element.querySelector("[data-sidebar-pin-icon]");
    if (pinIcon) {
      pinIcon.classList.toggle("rotate-180", this.isExpanded);
    }

    const openIcon = this.element.querySelector("[data-sidebar-hamburger-icon='open']");
    const closeIcon = this.element.querySelector("[data-sidebar-hamburger-icon='close']");
    if (openIcon && closeIcon) {
      openIcon.classList.toggle("hidden", this.isExpanded);
      closeIcon.classList.toggle("hidden", !this.isExpanded);
    }
  }

  // —— Responsive ————————————————————————————————————————

  _handleMediaChange() {
    // Re-apply state when crossing the breakpoint so the correct
    // width (0 vs 4rem) is used for the collapsed state.
    this._applyState();
  }
}
